// /home/w3d/web3reg-com/src/pages/PrivacyPolicy.js
import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faShieldAlt, faLock, faDatabase, faGlobe, faUserLock, faKey, faCode, faBalanceScale } from '@fortawesome/free-solid-svg-icons';
import Footer from '../components/Footer';
import GlobalNav from '../components/GlobalNav';

const PrivacyPolicy = () => {
  return (
    <div className="landing-page">
      <GlobalNav />
      
      <section className="hero" style={{ padding: '4rem 2rem 2rem 2rem' }}>
        <div className="container" style={{ maxWidth: '1000px' }}>
          <h1 className="gold-logo" style={{ fontSize: '3.5rem', marginBottom: '1rem' }}>
            <FontAwesomeIcon icon={faShieldAlt} style={{ marginRight: '1rem' }} />
            Privacy Policy
          </h1>
          <p style={{ fontSize: '1.1rem', color: 'var(--text-secondary)', maxWidth: '800px', margin: '0 auto 2rem' }}>
            Web3Reg is built on principles of decentralization and user sovereignty. 
            This policy explains how we handle—or rather, don't handle—your data.
          </p>
          <div style={{ 
            background: 'rgba(255,215,0,0.05)', 
            padding: '1.5rem', 
            borderRadius: '12px', 
            border: '1px solid var(--gold)',
            marginBottom: '2rem'
          }}>
            <p style={{ color: 'var(--gold)', fontSize: '1.1rem', margin: 0, fontStyle: 'italic' }}>
              <FontAwesomeIcon icon={faLock} style={{ marginRight: '0.5rem' }} />
              "We believe in Web3: Your keys, your data, your identity."
            </p>
          </div>
        </div>
      </section>

      <section className="privacy-content" style={{ padding: '2rem 2rem 4rem 2rem' }}>
        <div className="container" style={{ maxWidth: '1000px' }}>
          <div style={{ 
            background: 'var(--card-bg)', 
            borderRadius: '15px', 
            padding: '2.5rem',
            border: '1px solid var(--border)',
            boxShadow: '0 10px 40px rgba(0,0,0,0.3)'
          }}>
            {/* Last Updated */}
            <div style={{ 
              textAlign: 'right', 
              color: 'var(--text-secondary)', 
              fontSize: '0.9rem',
              marginBottom: '2rem',
              paddingBottom: '1rem',
              borderBottom: '1px solid var(--border)'
            }}>
              Last Updated: {new Date().toLocaleDateString('en-US', { 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })}
            </div>

            {/* Policy Sections */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '2.5rem' }}>
              {/* Section 1 */}
              <div>
                <h2 style={{ color: 'var(--gold)', fontSize: '1.8rem', marginBottom: '1rem' }}>
                  <FontAwesomeIcon icon={faGlobe} style={{ marginRight: '0.75rem' }} />
                  1. Web3 Principles
                </h2>
                <p style={{ color: 'var(--text-secondary)', lineHeight: '1.7', marginBottom: '1rem' }}>
                  Unlike traditional web services, Web3Reg embraces decentralization. We don't own or control user data. 
                  Users maintain their own cryptographic keys and identity is self-sovereign via ENS domains.
                </p>
                <ul style={{ color: 'var(--text-secondary)', paddingLeft: '1.5rem' }}>
                  <li>No centralized databases of personal information</li>
                  <li>Identity controlled via .zpost.eth or .xbsys.eth ENS domains</li>
                  <li>Military-grade embedded Zwallet security</li>
                  <li>Your profile data stored on Ethereum via ENS records</li>
                </ul>
              </div>

              {/* Section 2 */}
              <div>
                <h2 style={{ color: 'var(--gold)', fontSize: '1.8rem', marginBottom: '1rem' }}>
                  <FontAwesomeIcon icon={faDatabase} style={{ marginRight: '0.75rem' }} />
                  2. Information We Don't Collect
                </h2>
                <p style={{ color: 'var(--text-secondary)', lineHeight: '1.7', marginBottom: '1rem' }}>
                  We deliberately minimize data collection:
                </p>
                <div style={{ 
                  background: 'rgba(128,0,0,0.1)', 
                  padding: '1.5rem', 
                  borderRadius: '10px',
                  border: '1px solid var(--dark-red)',
                  marginBottom: '1rem'
                }}>
                  <ul style={{ color: 'var(--text-secondary)', paddingLeft: '1.5rem' }}>
                    <li>❌ No personal identity information (name, email, phone)</li>
                    <li>❌ No browsing history or behavioral tracking</li>
                    <li>❌ No IP address logging (where possible)</li>
                    <li>❌ No social connections or content metadata</li>
                    <li>❌ No advertising or tracking cookies</li>
                  </ul>
                </div>
              </div>

              {/* Section 3 */}
              <div>
                <h2 style={{ color: 'var(--gold)', fontSize: '1.8rem', marginBottom: '1rem' }}>
                  <FontAwesomeIcon icon={faUserLock} style={{ marginRight: '0.75rem' }} />
                  3. User Control & Sovereignty
                </h2>
                <p style={{ color: 'var(--text-secondary)', lineHeight: '1.7', marginBottom: '1rem' }}>
                  As a Web3 user, you maintain complete control:
                </p>
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', 
                  gap: '1.5rem',
                  marginBottom: '1.5rem'
                }}>
                  <div style={{ 
                    background: 'rgba(255,215,0,0.05)', 
                    padding: '1.5rem', 
                    borderRadius: '10px',
                    border: '1px solid var(--gold)'
                  }}>
                    <h3 style={{ color: 'var(--gold)', marginBottom: '0.5rem' }}>Your Wallet</h3>
                    <p style={{ color: 'var(--text-secondary)', fontSize: '0.95rem' }}>
                      Your Ethereum wallet is non-custodial. We never see your private keys or seed phrases.
                    </p>
                  </div>
                  <div style={{ 
                    background: 'rgba(255,215,0,0.05)', 
                    padding: '1.5rem', 
                    borderRadius: '10px',
                    border: '1px solid var(--gold)'
                  }}>
                    <h3 style={{ color: 'var(--gold)', marginBottom: '0.5rem' }}>Your Identity</h3>
                    <p style={{ color: 'var(--text-secondary)', fontSize: '0.95rem' }}>
                      Your .zpost.eth or .xbsys.eth ENS domain is your sovereign digital identity.
                    </p>
                  </div>
                  <div style={{ 
                    background: 'rgba(255,215,0,0.05)', 
                    padding: '1.5rem', 
                    borderRadius: '10px',
                    border: '1px solid var(--gold)'
                  }}>
                    <h3 style={{ color: 'var(--gold)', marginBottom: '0.5rem' }}>Your Data</h3>
                    <p style={{ color: 'var(--text-secondary)', fontSize: '0.95rem' }}>
                      Your profile, content, and connections are stored on Ethereum via ENS.
                    </p>
                  </div>
                </div>
              </div>

              {/* Section 4 */}
              <div>
                <h2 style={{ color: 'var(--gold)', fontSize: '1.8rem', marginBottom: '1rem' }}>
                  <FontAwesomeIcon icon={faKey} style={{ marginRight: '0.75rem' }} />
                  4. Blockchain Transparency
                </h2>
                <p style={{ color: 'var(--text-secondary)', lineHeight: '1.7', marginBottom: '1rem' }}>
                  Web3 operates on public blockchains. The following is inherently public:
                </p>
                <ul style={{ color: 'var(--text-secondary)', paddingLeft: '1.5rem', marginBottom: '1.5rem' }}>
                  <li>Ethereum addresses (0x...)</li>
                  <li>ENS domain registrations and transfers</li>
                  <li>Transaction history (visible on Etherscan)</li>
                  <li>Smart contract interactions</li>
                  <li>Gas fees and network operations</li>
                </ul>
                <div style={{ 
                  background: 'rgba(0,100,255,0.05)', 
                  padding: '1.5rem', 
                  borderRadius: '10px',
                  border: '1px solid #0064ff',
                  fontStyle: 'italic'
                }}>
                  <p style={{ color: 'var(--text-secondary)', margin: 0 }}>
                    <strong>Note:</strong> While transactions are public, you can maintain pseudonymity. 
                    Your real-world identity is not linked unless you choose to reveal it.
                  </p>
                </div>
              </div>

              {/* Section 5 */}
              <div>
                <h2 style={{ color: 'var(--gold)', fontSize: '1.8rem', marginBottom: '1rem' }}>
                  <FontAwesomeIcon icon={faCode} style={{ marginRight: '0.75rem' }} />
                  5. Open Source & Audits
                </h2>
                <p style={{ color: 'var(--text-secondary)', lineHeight: '1.7' }}>
                  Our code is open source and regularly audited. You can verify:
                </p>
                <ul style={{ color: 'var(--text-secondary)', paddingLeft: '1.5rem' }}>
                  <li>Smart contract security audits</li>
                  <li>Zwallet encryption implementations</li>
                  <li>No hidden tracking or backdoors</li>
                  <li>Transparent protocol operations</li>
                </ul>
              </div>

              {/* Section 6 */}
              <div>
                <h2 style={{ color: 'var(--gold)', fontSize: '1.8rem', marginBottom: '1rem' }}>
                  <FontAwesomeIcon icon={faBalanceScale} style={{ marginRight: '0.75rem' }} />
                  6. Your Rights
                </h2>
                <p style={{ color: 'var(--text-secondary)', lineHeight: '1.7', marginBottom: '1rem' }}>
                  In the Web3 paradigm, you have enhanced rights:
                </p>
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', 
                  gap: '1rem',
                  marginBottom: '1.5rem'
                }}>
                  <div style={{ 
                    background: 'linear-gradient(135deg, rgba(255,215,0,0.1) 0%, rgba(128,0,0,0.1) 100%)', 
                    padding: '1.25rem', 
                    borderRadius: '8px'
                  }}>
                    <h3 style={{ color: 'var(--gold)', fontSize: '1.1rem', marginBottom: '0.5rem' }}>Ownership</h3>
                    <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', margin: 0 }}>
                      Complete ownership of your identity, content, and digital assets.
                    </p>
                  </div>
                  <div style={{ 
                    background: 'linear-gradient(135deg, rgba(255,215,0,0.1) 0%, rgba(128,0,0,0.1) 100%)', 
                    padding: '1.25rem', 
                    borderRadius: '8px'
                  }}>
                    <h3 style={{ color: 'var(--gold)', fontSize: '1.1rem', marginBottom: '0.5rem' }}>Portability</h3>
                    <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', margin: 0 }}>
                      Take your identity and data to any compatible Web3 service.
                    </p>
                  </div>
                  <div style={{ 
                    background: 'linear-gradient(135deg, rgba(255,215,0,0.1) 0%, rgba(128,0,0,0.1) 100%)', 
                    padding: '1.25rem', 
                    borderRadius: '8px'
                  }}>
                    <h3 style={{ color: 'var(--gold)', fontSize: '1.1rem', marginBottom: '0.5rem' }}>Pseudonymity</h3>
                    <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', margin: 0 }}>
                      Right to participate without revealing real-world identity.
                    </p>
                  </div>
                </div>
              </div>

              {/* Web3 Commitment Section */}
              <div style={{ 
                background: 'linear-gradient(135deg, var(--card-bg) 0%, rgba(0,0,0,0.3) 100%)', 
                padding: '2rem', 
                borderRadius: '12px',
                border: '1px solid var(--gold)',
                textAlign: 'center',
                marginTop: '2rem'
              }}>
                <h3 style={{ color: 'var(--gold)', fontSize: '1.5rem', marginBottom: '1rem' }}>
                  Web3 Privacy Commitment
                </h3>
                <p style={{ color: 'var(--text-secondary)', marginBottom: '1rem' }}>
                  We are committed to the principles of decentralization, transparency, and user sovereignty.
                  Our architecture ensures that privacy is not just a feature, but the foundation of our platform.
                </p>
                <div style={{ 
                  display: 'inline-block', 
                  padding: '0.75rem 1.5rem', 
                  background: 'rgba(255,215,0,0.1)', 
                  color: 'var(--gold)', 
                  borderRadius: '30px',
                  fontWeight: 'bold',
                  border: '1px solid var(--gold)',
                  fontSize: '0.9rem'
                }}>
                  Privacy by Design
                </div>
                <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', marginTop: '1rem', fontStyle: 'italic' }}>
                  "In Web3, privacy isn't a feature—it's the foundation."
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PrivacyPolicy;
